import React, { useEffect, useMemo, useState } from 'react';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { Accordion, AccordionDetails, AccordionSummary, Stack, Tab, Tabs, Typography } from '@mui/material';

import type { HealthCategoryConsultationStatus, HealthCategoryId } from '@shared/patient-intake-status';

import { usePatientIntakeStatusContext } from '../hooks';
import { ActiveCategorySection } from './ActiveCategorySection.tsx';

const pastHealthCategoriesFilter = (healthCategories: HealthCategoryConsultationStatus | null) => (category: string) =>
  healthCategories?.[category as HealthCategoryId] !== 'AWAITING_REVIEW';

export const PastHealthCategoriesSection: React.FC = () => {
  const { healthCategories, isHealthcareProvider } = usePatientIntakeStatusContext();
  const [activeHealthCategoryId, setActiveHealthCategoryId] = useState<HealthCategoryId | null>(null);
  const [expanded, setExpanded] = useState(false);

  const categoryList = useMemo(
    () =>
      Object.keys(healthCategories || {})
        .filter(pastHealthCategoriesFilter(healthCategories))
        .sort() as HealthCategoryId[],
    [healthCategories],
  );

  useEffect(() => {
    if (categoryList.length > 0) {
      setActiveHealthCategoryId(categoryList[0] ?? null);
    }
  }, [categoryList]);

  const handleToggleExpanded = () => {
    setExpanded((prev) => !prev);
  };

  const handleTabChange = (newValue: HealthCategoryId | null) => {
    if (newValue === activeHealthCategoryId) {
      return;
    }

    setActiveHealthCategoryId(newValue);
  };

  if (!healthCategories || !categoryList.length || !isHealthcareProvider) {
    return null;
  }

  return (
    <Accordion expanded={expanded} onChange={handleToggleExpanded}>
      <AccordionSummary sx={{ px: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
            {categoryList.length} Past Form{categoryList.length === 1 ? '' : 's'}
          </Typography>

          <KeyboardArrowDown
            style={{
              transition: 'transform 0.1s ease-in-out',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              opacity: 0.2,
            }}
          />
        </Stack>
      </AccordionSummary>

      <AccordionDetails>
        <Tabs
          id="past-consultations-tabs"
          value={activeHealthCategoryId}
          onChange={(_, newValue) => handleTabChange(newValue)}
          variant="fullWidth"
          scrollButtons={true}
          allowScrollButtonsMobile={true}
          sx={{ mb: 2 }}
        >
          {categoryList.map((category) => (
            <Tab key={category} label={category} value={category} sx={{ textTransform: 'capitalize' }} />
          ))}
        </Tabs>

        <ActiveCategorySection activeHealthCategoryId={activeHealthCategoryId} />
      </AccordionDetails>
    </Accordion>
  );
};
